import React, { useState, useEffect, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import styles from './ResultTeen.module.css';
import "./Result.css";

// Helper: map card code to image
const getCardImage = (cardString) => {
  if (!cardString) return null;
  return `/assets/img/cards/${cardString}.png`;
};

// Helper: split cards into Player A / Player B (indexes 0,2,4 vs 1,3,5)
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [] };

  const cards = cardString.split(',');
  const playerACards = [0, 2, 4].map(i => {
    const card = cards[i]?.trim();
    return card ? getCardImage(card) : null;
  }).filter(Boolean);

  const playerBCards = [1, 3, 5].map(i => {
    const card = cards[i]?.trim();
    return card ? getCardImage(card) : null;
  }).filter(Boolean);

  return { playerA: playerACards, playerB: playerBCards };
};

// Helper: parse rdesc (Winner#Suits#Odd/Even#Consecutive)
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};

  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const suits = parts[1] || "";
  const oddEven = parts[2] || "";
  const consecutive = parts[3] || "";

  return { winner, suits, oddEven, consecutive };
};

const ResultTeen = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  // Fetch last results on mount (type: teen)
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teen');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mappedResults = response.data.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
            }));
            setLastResults(mappedResults);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mappedResults = response.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
            }));
            setLastResults(mappedResults);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch detail results when navigating between results
  const fetchDetailForIndex = useCallback(async (index) => {
    if (index >= 0 && index < lastResults.length && lastResults[index]?.mid) {
      try {
        const response = await getDetailResults('teen', lastResults[index].mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            return response.data.data.t1;
          } else if (response?.data?.t1) {
            return response.data.t1;
          }
        }
      } catch (error) {
        console.error(`Error fetching detail for index ${index}:`, error);
      }
    }
    return null;
  }, [lastResults]);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setCurrentResultIndex(index);
    setShowModal(true);
    setLoading(true);

    const mid = resultItem?.mid;
    
    if (mid) {
      try {
        const response = await getDetailResults('teen', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
    setCurrentResultIndex(0);
  };

  const goToPrevResult = useCallback(async () => {
    if (currentResultIndex > 0) {
      const newIndex = currentResultIndex - 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  const goToNextResult = useCallback(async () => {
    if (currentResultIndex < lastResults.length - 1) {
      const newIndex = currentResultIndex + 1;
      setCurrentResultIndex(newIndex);
      setSelectedResult(lastResults[newIndex]);
      setLoading(true);
      const detail = await fetchDetailForIndex(newIndex);
      if (detail) {
        setDetailResult(detail);
      }
      setLoading(false);
    }
  }, [currentResultIndex, lastResults, fetchDetailForIndex]);

  // Format modal data
  const modalData = React.useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : { playerA: [], playerB: [] };
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" || selectedResult?.win === "A" ? "Player A" : selectedResult === "B" || selectedResult?.win === "B" ? "Player B" : "");

    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A" || selectedResult?.win === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B" || selectedResult?.win === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      resultDetails: {
        winner: winner,
        oddEven: rdescData.oddEven || "",
        consecutive: rdescData.consecutive || "",
      },
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : (data && data.length > 0 
        ? data.map((res, idx) => ({ win: res === "1" ? "A" : res === "2" ? "B" : res, mid: null }))
        : []);

  return (
    <>
      {/* Last Results Section */}
      {allResults.length > 0 && (
        <div className={styles.lastResults}>
          {allResults.map((resultItem, index) => {
            const winValue = resultItem.win || resultItem;
            const displayValue = typeof winValue === 'string' ? winValue : (winValue === "1" ? "A" : winValue === "2" ? "B" : winValue);

            return (
              <span
                key={`${resultItem.mid || index}-${index}`}
                className={`${styles.resultCircle} ${displayValue === "A" ? styles.playerA : styles.playerB}`}
                onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
                style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
              >
                {displayValue}
              </span>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Teenpatti 1-day Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>

            <div className="row bottom-border-casino m-t-10">
              <div className="col-md-12">
                <div className="player-image-container row">
                  <div className="col-md-6 text-center player-number">
                    <h4>Player A</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                      {modalData.playerA?.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player A Card ${index + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/card-results/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerA?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 text-center player-number">
                    <h4>Player B</h4>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '2px' }}>
                      {modalData.playerB?.cards?.map((card, index) => (
                        card && (
                          <img
                            key={index}
                            src={card}
                            alt={`Player B Card ${index + 1}`}
                            onError={(e) => {
                              const cardName = card.split('/').pop();
                              const fallbackPath = `/assets/img/card-results/${cardName}`;
                              if (e.target.src !== fallbackPath) {
                                e.target.src = fallbackPath;
                              } else {
                                e.target.style.display = 'none';
                              }
                            }}
                          />
                        )
                      ))}
                    </div>
                    <div className="col-md-12 text-center">
                      {modalData.playerB?.isWinner ? (
                        <label className="winner-label bg-success m-t-20">
                          <i className="fas fa-trophy m-r-5"></i>Winner
                        </label>
                      ) : (
                        <label className="m-t-20">&nbsp;</label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeen;

